import { useMemo, useRef, useState } from 'react';
import { EXPORT_PRESETS } from '@cinegrid/core';
import { downloadBlob, exportComposition } from '../../codec/exportComposition';
import { supportsMediaRecorder, supportsWebCodecs } from '../../codec/importMedia';
import { OfflineRenderer } from '../../engine/offlineRenderer';
import { formatTimecode, projectDuration } from '../../timeline/ops';
import { useEditorStore } from '../../state/editorStore';
import { PanelShell, SectionLabel } from '../controls/PanelShell';

function statusLabel(status: string): string {
  switch (status) {
    case 'preparing':
      return 'Preparing';
    case 'rendering':
    case 'encoding':
      return 'Rendering';
    case 'muxing':
      return 'Muxing';
    case 'done':
      return 'Done';
    case 'cancelled':
      return 'Cancelled';
    case 'error':
      return 'Error';
    default:
      return status === 'idle' ? 'Ready' : status;
  }
}

export function ExportPanel() {
  const project = useEditorStore((s) => s.project);
  const inPoint = useEditorStore((s) => s.inPoint);
  const outPoint = useEditorStore((s) => s.outPoint);
  const setInPoint = useEditorStore((s) => s.setInPoint);
  const setOutPoint = useEditorStore((s) => s.setOutPoint);
  const setPlayhead = useEditorStore((s) => s.setPlayhead);
  const setPlaying = useEditorStore((s) => s.setPlaying);
  const [presetId, setPresetId] = useState('match-timeline');
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle');
  const [method, setMethod] = useState<string>('');
  const abortRef = useRef<AbortController | null>(null);
  const rendererRef = useRef<OfflineRenderer | null>(null);

  const fullDur = useMemo(() => projectDuration(project), [project]);
  const rangeStart = inPoint ?? 0;
  const rangeEnd = outPoint ?? fullDur;
  const exportDur = Math.max(0, rangeEnd - rangeStart);
  const fps = project.settings.frameRate;
  const busy = status === 'rendering' || status === 'preparing' || status === 'muxing' || status === 'encoding';

  const start = async () => {
    // Ensure we have a usable In/Out range
    let startT = inPoint;
    let endT = outPoint;
    if (startT == null && endT == null) {
      startT = 0;
      endT = fullDur;
      setInPoint(0);
      setOutPoint(fullDur);
    } else if (startT == null) {
      startT = 0;
      setInPoint(0);
    } else if (endT == null) {
      endT = fullDur;
      setOutPoint(fullDur);
    }
    if (endT! <= startT!) {
      setStatus('error');
      return;
    }

    abortRef.current = new AbortController();
    setStatus('preparing');
    setProgress(0);
    setMethod('');
    setPlaying(false);
    setPlayhead(startT!);

    rendererRef.current?.destroy();
    const renderer = new OfflineRenderer();
    rendererRef.current = renderer;

    let usedMethod = '';
    try {
      const blob = await exportComposition({
        project,
        presetId,
        rangeStart: startT!,
        rangeEnd: endT!,
        canvas: renderer.canvas,
        signal: abortRef.current.signal,
        renderFrame: async (time, canvas) => {
          setPlayhead(time);
          await renderer.render(project, time, canvas.width, canvas.height);
          // Let React paint the playhead / viewer before the next frame
          await new Promise<void>((r) => requestAnimationFrame(() => r()));
        },
        onProgress: (p) => {
          setStatus(p.status);
          setProgress(p.progress);
          if (p.method) {
            usedMethod = p.method;
            setMethod(p.method);
          }
        },
      });
      setPlayhead(endT!);
      const preset = EXPORT_PRESETS.find((p) => p.id === presetId);
      const ext =
        preset?.container === 'webm' || usedMethod === 'mediarecorder' ? 'webm' : 'mp4';
      downloadBlob(blob, `${project.name.replace(/\s+/g, '_')}.${ext}`);
      setStatus('done');
      setProgress(1);
    } catch (err) {
      setStatus(err instanceof Error && err.message === 'cancelled' ? 'cancelled' : 'error');
    } finally {
      rendererRef.current?.destroy();
      rendererRef.current = null;
    }
  };

  return (
    <PanelShell title="Export">
      <SectionLabel>In / Out Range</SectionLabel>
      <div className="space-y-1 border-b border-hairline p-3 text-2xs">
        <div className="flex justify-between text-text-low">
          <span>Mark In</span>
          <span className="timecode text-text-high">
            {inPoint != null ? formatTimecode(inPoint, fps) : '— (uses 0)'}
          </span>
        </div>
        <div className="flex justify-between text-text-low">
          <span>Mark Out</span>
          <span className="timecode text-text-high">
            {outPoint != null ? formatTimecode(outPoint, fps) : '— (uses end)'}
          </span>
        </div>
        <div className="flex justify-between pt-1 text-text-low">
          <span>Export length</span>
          <span className="timecode text-accent">{formatTimecode(exportDur, fps)}</span>
        </div>
        <p className="pt-1 text-text-low">
          Drag I / O on the timeline ruler, or set them from the viewer (I / O keys).
        </p>
      </div>

      <SectionLabel>Presets</SectionLabel>
      <div className="max-h-40 overflow-auto">
        {EXPORT_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPresetId(p.id)}
            className={`flex w-full flex-col items-start border-b border-hairline px-3 py-2 text-left ${
              presetId === p.id ? 'bg-raised' : 'hover:bg-raised'
            }`}
          >
            <span className="text-xs text-text-high">{p.label}</span>
            <span className="text-2xs text-text-low">
              {p.width || 'timeline'}×{p.height || 'timeline'} · {p.frameRate || 'fps'} ·{' '}
              {p.container.toUpperCase()}
            </span>
          </button>
        ))}
      </div>
      <SectionLabel>Engine</SectionLabel>
      <div className="space-y-1 p-3 text-2xs text-text-low">
        <p>WebCodecs: {supportsWebCodecs() ? 'available' : 'unavailable'}</p>
        <p>MediaRecorder fallback: {supportsMediaRecorder() ? 'available' : 'unavailable'}</p>
      </div>

      {(busy || status === 'done' || status === 'error' || status === 'cancelled') && (
        <div className="border-t border-hairline px-3 py-2">
          <div className="mb-1 flex items-center justify-between text-2xs">
            <span className="text-text-high">{statusLabel(status)}</span>
            <span className="timecode text-text-low">{(progress * 100).toFixed(0)}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden bg-raised">
            <div
              className={`h-full transition-[width] duration-150 ${
                status === 'error' ? 'bg-danger' : status === 'done' ? 'bg-playhead' : 'bg-accent'
              }`}
              style={{ width: `${Math.min(100, Math.max(0, progress * 100))}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 border-t border-hairline p-3">
        <button
          type="button"
          onClick={() => void start()}
          disabled={busy}
          className="bg-accent px-3 py-1.5 text-2xs font-semibold text-base disabled:opacity-40"
        >
          Export
        </button>
        <button
          type="button"
          onClick={() => abortRef.current?.abort()}
          className="border border-hairline px-3 py-1.5 text-2xs text-text-low hover:text-danger"
        >
          Cancel
        </button>
        <span className="timecode ml-auto text-2xs text-text-low">
          {status === 'idle' ? 'Ready' : `${statusLabel(status)} ${(progress * 100).toFixed(0)}%`}
          {method ? ` · ${method}` : ''}
        </span>
      </div>
    </PanelShell>
  );
}
